import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Send, Loader2, LifeBuoy, Bot } from "lucide-react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import useAuthStore from "../store/authStore";

const CATEGORY_LABEL = {
  PICKUP_ISSUE: "Pickup issue", DELIVERY_ISSUE: "Delivery issue",
  PAYMENT_ISSUE: "Payment issue", PARCEL_DAMAGE: "Parcel damage",
  WRONG_ADDRESS: "Wrong address", REFUND_REQUEST: "Refund request",
  AGENT_ISSUE: "Agent issue", GENERAL_QUESTION: "General question",
  OTHER: "Other",
};

const STATUS_PILL = {
  open:      "bg-orange-50 text-orange-700 border-orange-200",
  in_review: "bg-blue-50 text-blue-700 border-blue-200",
  resolved:  "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const STATUS_LABEL = { open: "Open", in_review: "In review", resolved: "Resolved" };

const MAX = 2000;

function formatTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

function MessageBubble({ msg, isMine }) {
  const isSystem = msg.author_type === "system";
  const align = isMine ? "items-end" : "items-start";
  const bubble = isMine
    ? "bg-[#FF6B00] text-white rounded-2xl rounded-br-sm"
    : isSystem
    ? "bg-slate-50 text-slate-700 border border-slate-200 rounded-2xl rounded-bl-sm"
    : "bg-white text-slate-900 border border-slate-200 rounded-2xl rounded-bl-sm";

  return (
    <div className={`flex flex-col ${align}`} data-testid={`support-msg-${msg.id}`}>
      <div className={`max-w-[78%] px-4 py-2.5 shadow-sm ${bubble}`}>
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.body}</p>
      </div>
      <div className={`flex items-center gap-1.5 mt-1 text-[11px] text-slate-400 ${isMine ? "flex-row-reverse" : ""}`}>
        {isSystem && <Bot className="h-3 w-3" />}
        <span className="font-medium">
          {isMine ? "You" : msg.author_name || (msg.author_type === "admin" ? "Support" : "System")}
        </span>
        <span>·</span>
        <span>{formatTs(msg.created_at)}</span>
      </div>
    </div>
  );
}

export default function SupportTicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [ticket, setTicket]   = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  };

  const load = async () => {
    try {
      const [tRes, mRes] = await Promise.all([
        api.get(`/customer/support/tickets/${ticketId}`),
        api.get(`/customer/support/tickets/${ticketId}/messages`),
      ]);
      setTicket(tRes.data);
      setMessages(mRes.data.messages || []);
      // Mark admin/system messages as read (best-effort, no toast)
      api.patch(`/customer/support/tickets/${ticketId}/messages/read`).catch(() => {});
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

  const send = async (e) => {
    e?.preventDefault?.();
    const trimmed = body.trim();
    if (!trimmed || sending) return;
    setSending(true);
    // Optimistic
    const optimistic = {
      id:    `temp-${Date.now()}`,
      author_type: "customer",
      author_name: user?.name || "You",
      body:  trimmed,
      created_at: new Date().toISOString(),
      read_by_other_party: false,
    };
    setMessages((prev) => [...prev, optimistic]);
    setBody("");
    scrollToBottom();
    try {
      const { data } = await api.post(`/customer/support/tickets/${ticketId}/messages`, { body: trimmed });
      // Replace optimistic with real
      setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? data.message : m)));
      if (data.ticket) setTicket((t) => ({ ...t, status: data.ticket.status }));
    } catch (err) {
      // Roll back optimistic
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setBody(trimmed);
    } finally {
      setSending(false);
      scrollToBottom();
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="support-thread-loading">
        <div className="h-9 w-32 bg-slate-200 rounded mb-6 animate-pulse" />
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded-2xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="support-thread-404">
        <LifeBuoy className="h-10 w-10 text-slate-300 mx-auto" />
        <h2 className="font-['Outfit'] text-xl font-bold mt-4">Ticket not found</h2>
        <p className="text-sm text-slate-500 mt-1">This ticket doesn't exist or you don't have access.</p>
        <Button className="mt-6" variant="outline" onClick={() => navigate("/dashboard")} data-testid="support-thread-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to dashboard
        </Button>
      </div>
    );
  }

  const status = ticket?.status || "open";
  const isResolved = status === "resolved";

  return (
    <div
      className="max-w-2xl mx-auto flex flex-col h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)]"
      data-testid="support-thread-page"
    >
      {/* Sticky header */}
      <div className="bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between gap-3 shrink-0">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-[#FF6B00]"
          data-testid="support-thread-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="text-center flex-1 min-w-0">
          <p className="font-mono text-xs font-semibold text-slate-900 truncate">{ticketId}</p>
          <p className="text-[11px] text-slate-500 truncate">
            {CATEGORY_LABEL[ticket?.category] || ticket?.category || "Support"}
          </p>
        </div>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${STATUS_PILL[status] || "bg-slate-50 text-slate-600 border-slate-200"}`}
          data-testid="support-thread-status"
        >
          {STATUS_LABEL[status] || status}
        </span>
      </div>

      {/* Scrollable thread */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto bg-slate-50 px-4 py-5 space-y-4"
        data-testid="support-thread-messages"
      >
        {messages.map((m) => (
          <MessageBubble key={m.id} msg={m} isMine={m.author_type === "customer"} />
        ))}
      </div>

      {/* Reply box */}
      <form
        onSubmit={send}
        className="bg-white border-t border-slate-100 px-3 py-3 shrink-0"
        data-testid="support-thread-reply-form"
      >
        {isResolved && (
          <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 mb-2 text-center">
            This ticket has been resolved. Sending a reply will reopen it.
          </p>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, MAX))}
            rows={1}
            maxLength={MAX}
            placeholder="Type your reply…"
            className="flex-1 resize-none px-3 py-2.5 rounded-2xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/40 min-h-[44px] max-h-32"
            onInput={(e) => {
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 128) + "px";
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            data-testid="support-thread-input"
          />
          <Button
            type="submit"
            disabled={!body.trim() || sending}
            className="bg-[#FF6B00] hover:bg-orange-600 text-white rounded-full p-2.5 h-11 w-11 shrink-0 disabled:opacity-40"
            data-testid="support-thread-send-btn"
            aria-label="Send"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        <div className="flex items-center justify-between mt-1 px-2">
          <Link
            to="/dashboard/contact-us"
            className="text-[11px] text-slate-400 hover:text-[#FF6B00]"
          >
            ↑ New support request
          </Link>
          <span className={`text-[11px] tabular-nums ${body.length >= MAX ? "text-rose-600 font-semibold" : "text-slate-400"}`}>
            {body.length} / {MAX}
          </span>
        </div>
      </form>
    </div>
  );
}
